import { jsx, jsxs } from 'react/jsx-runtime';
import * as React from 'react';
import { c as classNames, E as EGDSContext, b as EGDSContextWrapper } from '../theme-provider/index-chunk.esm.js';
import styles from '../styles/uitk-egds-menu.css';
import { v as validatePropType } from '../validate-prop-types/index-chunk.esm.js';
import { a as EGDSIcon } from '../icon/index-chunk.esm.js';
import { a as analyticsTracker } from '../analytics-tracker/index-chunk.esm.js';

const getClassNameList$5 = classNames.bind(styles);
class EGDSMenu extends React.Component {
    static displayName = 'EGDSMenu';
    static defaultProps = {
        isOpen: false,
        disableBodyClickHandler: false,
    };
    currentIndex = 0;
    menuContainerRef = React.createRef();
    constructor(props) {
        super(props);
        this.state = {
            animate: false,
            mounted: false,
        };
        this.toggleMenu = this.toggleMenu.bind(this);
        this.handleClickEvent = this.handleClickEvent.bind(this);
        this.onFauxInputClick = this.onFauxInputClick.bind(this);
        this.onFauxInputKeyPress = this.onFauxInputKeyPress.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
    }
    componentDidMount() {
        this.setState({
            mounted: true,
        });
    }
    componentWillUnmount() {
        const { body } = document;
        body.removeEventListener('click', this.handleClickEvent);
        body.removeEventListener('keydown', this.onKeyDown);
        body.removeEventListener('keyup', this.onKeyUp);
    }
    handleClickEvent(e) {
        const { onDismiss, disableBodyClickHandler } = this.props;
        if (disableBodyClickHandler) {
            return;
        }
        // Controlled compontent
        const menuContainerRef = this.menuContainerRef.current;
        const menuWrapper = menuContainerRef && menuContainerRef.closest('.uitk-menu');
        // Clicked element references
        let { target } = e;
        /**
         * Checks if the menu is within a shadow Dom and if so, sets the target to the composed path rather than the root.
         * This was added because there was an edge case where the menu is within a shadow dom and would open and dismiss on the same click.
         */
        if (e.target.shadowRoot) {
            target = e.composedPath()[0];
        }
        const isClickedCheckbox = target.classList.contains('uitk-menu-checkbox');
        const clickedMenuWrapper = target.closest('.uitk-menu');
        const clickedMenuTrigger = clickedMenuWrapper && clickedMenuWrapper.querySelector('.uitk-menu-trigger');
        if ((clickedMenuTrigger && menuWrapper && !menuWrapper.contains(clickedMenuTrigger)) ||
            (!isClickedCheckbox && !target.closest('.uitk-menu'))) {
            onDismiss && onDismiss();
            this.toggleMenu(e);
        }
    }
    onKeyDown(e) {
        let target;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            target = document.activeElement;
            if (target.classList.contains('uitk-field-select') ||
                (target.classList.contains('uitk-field-input') && target.type === 'number')) {
                return;
            }
            e.preventDefault();
        }
        const { onDismiss } = this.props;
        if (e.key !== 'Escape') {
            return;
        }
        if (onDismiss) {
            onDismiss();
        }
        this.toggleMenu(e);
    }
    onKeyUp(e) {
        const { onDismiss } = this.props;
        const parent = this.menuContainerRef;
        let target;
        if (e.shiftKey && e.key === 'Tab') {
            target = document.activeElement;
            if (target.classList.contains('uitk-menu-trigger')) {
                return false;
            }
        }
        if (parent.current) {
            const listItems = parent.current.children[0].children;
            if (e.key === 'ArrowDown') {
                target = document.activeElement;
                if (target.classList.contains('uitk-menu-trigger')) {
                    this.currentIndex = 0;
                    listItems[this.currentIndex].focus();
                }
                else if (target.classList.contains('uitk-list-item')) {
                    const elemToFocusOn = listItems[this.currentIndex + 1];
                    if (elemToFocusOn) {
                        this.currentIndex += 1;
                        elemToFocusOn.focus();
                    }
                }
            }
            else if (e.key === 'ArrowUp') {
                target = document.activeElement;
                if (target.classList.contains('uitk-menu-trigger')) {
                    return false;
                }
                if (target.classList.contains('uitk-list-item')) {
                    const elemToFocusOn = listItems[this.currentIndex - 1];
                    if (elemToFocusOn) {
                        this.currentIndex -= 1;
                        elemToFocusOn.focus();
                    }
                }
            }
            else if (e.key === 'Tab') {
                target = document.activeElement;
                for (let i = 0; i <= listItems.length; i++) {
                    if (target === listItems[i]) {
                        this.currentIndex = i;
                    }
                }
                const isDescendant = this.isDescendant(parent, target);
                if (!isDescendant) {
                    if (onDismiss) {
                        onDismiss();
                    }
                    this.toggleMenu(e);
                }
            }
        }
    }
    onFauxInputClick(e) {
        if (e.target.classList.contains('uitk-faux-input')) {
            this.toggleMenu(e);
        }
    }
    onFauxInputKeyPress(e) {
        const keyValue = e.key;
        if (keyValue !== 'Enter') {
            if (e.target.classList.contains('uitk-faux-input')) {
                this.toggleMenu(e);
            }
        }
    }
    toggleMenu(e) {
        const { id, onTriggerClick } = this.props;
        if (onTriggerClick) {
            onTriggerClick(e, {
                id,
            });
        }
        this.setState({
            animate: true,
        });
    }
    isDescendant(parent, child) {
        return !!parent.current.contains(child);
    }
    render() {
        const { children, className, id, isOpen } = this.props;
        const { animate, mounted } = this.state;
        const menuContainerClasses = ['uitk-menu-container'];
        const menuClasses = getClassNameList$5(className, 'uitk-menu', {
            'uitk-menu-mounted': mounted,
        });
        const triggerClassNames = getClassNameList$5('uitk-menu-checkbox', 'is-visually-hidden');
        let menuId = '';
        let childProps;
        let containerChildProps;
        const triggerProps = {
            className: triggerClassNames,
            onClick: this.toggleMenu,
        };
        if (!animate) {
            menuContainerClasses.push('animation-disabled');
        }
        if (id) {
            menuId = id;
        }
        if (mounted) {
            const { body } = document;
            if (isOpen) {
                menuContainerClasses.push('uitk-menu-open');
                body.addEventListener('click', this.handleClickEvent);
                body.addEventListener('keydown', this.onKeyDown);
                body.addEventListener('keyup', this.onKeyUp);
            }
            else {
                body.removeEventListener('click', this.handleClickEvent);
                body.removeEventListener('keydown', this.onKeyDown);
                body.removeEventListener('keyup', this.onKeyUp);
            }
        }
        const containerClasses = menuContainerClasses.join(' ');
        const menuChildren = React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) {
                return undefined;
            }
            switch (child.type) {
                case EGDSMenuTrigger:
                    childProps = child.props;
                    return (jsx(EGDSMenuTrigger, { ...childProps, mounted: mounted, onClick: this.toggleMenu, isOpen: isOpen, menuId: id }));
                case EGDSMenuContainer:
                    containerChildProps = child.props;
                    return (jsx(EGDSMenuContainer, { containerClasses: containerClasses, containerRef: this.menuContainerRef, isOpen: isOpen, ...containerChildProps }));
                default:
                    return false;
            }
        });
        if (!mounted) {
            const menuIdNoJs = menuId ? `-${menuId}` : '';
            return (jsxs("div", { className: menuClasses, children: [jsx("input", { type: "checkbox", value: "selected", id: `uitk-menu-no-js${menuIdNoJs}`, "aria-checked": isOpen, ...triggerProps }), menuChildren] }));
        }
        return (
        // eslint-disable-next-line no-warning-comments
        // TODO: https://jira.expedia.biz/browse/UDS-1879
        /* eslint-disable */
        jsx("div", { className: menuClasses, id: menuId, onClick: this.onFauxInputClick, onKeyPress: this.onFauxInputKeyPress, children: menuChildren }));
    }
}

const getClassNameList$4 = classNames.bind(styles);
var EGDSMenuTriggerAnalyticsId;
(function (EGDSMenuTriggerAnalyticsId) {
    EGDSMenuTriggerAnalyticsId["EXPAND"] = "Expand";
    EGDSMenuTriggerAnalyticsId["COLLAPSE"] = "Collapse";
})(EGDSMenuTriggerAnalyticsId || (EGDSMenuTriggerAnalyticsId = {}));
class EGDSMenuTrigger extends React.Component {
    static displayName = 'EGDSMenuTrigger';
    static baseClassName = 'uitk-menu-trigger';
    render() {
        const { mounted, isOpen, children, menuId, onClick, disableLocalAnalytics } = this.props;
        const localAnalyticsId = isOpen ? EGDSMenuTriggerAnalyticsId.COLLAPSE : EGDSMenuTriggerAnalyticsId.EXPAND;
        const childCount = React.Children.count(children);
        if (!children || childCount !== 1) {
            console.warn(`UitkMenuTrigger requires exactly one child component. ${childCount} children found.`);
            return null;
        }
        const menuTriggerChild = children;
        const { className, analytics } = menuTriggerChild.props;
        const cssClassNames = getClassNameList$4(EGDSMenuTrigger.baseClassName, className);
        let additionalProps = {};
        if (!mounted) {
            const menuIdNoJs = menuId ? `-${menuId}` : '';
            additionalProps = {
                'aria-hidden': 'true',
                className: cssClassNames,
                htmlFor: `uitk-menu-no-js${menuIdNoJs}`,
                tag: 'label',
            };
            return React.cloneElement(menuTriggerChild, additionalProps);
        }
        additionalProps = {
            analytics: analytics && !disableLocalAnalytics ? { localAnalyticsId, ...analytics } : undefined,
            'aria-expanded': isOpen,
            className: cssClassNames,
            onClick: (e) => {
                if (onClick) {
                    onClick(e, { menuId });
                }
                if (menuTriggerChild.props.onClick) {
                    menuTriggerChild.props.onClick(e, { menuId });
                }
            },
        };
        return React.cloneElement(menuTriggerChild, additionalProps);
    }
}

const EGDSHeadingTagMap = [
    ['h1', 'h1'],
    ['h2', 'h2'],
    ['h3', 'h3'],
    ['h4', 'h4'],
    ['h5', 'h5'],
    ['h6', 'h6'],
];
const defaultMenuHeadingTag = 'h3';
const defaultMenuListHeadingTag = 'h4';
const EGDSHeadingTags = new Map(EGDSHeadingTagMap);

const getClassNameList$3 = classNames.bind(styles);
var PositionType;
(function (PositionType) {
    PositionType["RIGHT"] = "right";
    PositionType["LEFT"] = "left";
})(PositionType || (PositionType = {}));
class EGDSMenuContainer extends React.Component {
    static displayName = 'EGDSMenuContainer';
    static defaultProps = {
        position: PositionType.RIGHT,
    };
    static contextType = EGDSContext;
    intersectionObserver;
    // Private resizeObserver: any;
    // private resizeObserverIsBound: boolean;
    // private resizeWidth: number;
    constructor(props) {
        super(props);
        this.state = {
            animationHasEnded: false,
            offset: {
                x: 0,
                y: 0,
            },
        };
        this.autoPositionCallback = this.autoPositionCallback.bind(this);
        this.adjustAlignment = this.adjustAlignment.bind(this);
        this.resetMenuContainer = this.resetMenuContainer.bind(this);
        this.onAnimationEndCallback = this.onAnimationEndCallback.bind(this);
        this.animationEndedStateCallback = this.animationEndedStateCallback.bind(this);
        this.unobserveIntersectionObserver = this.unobserveIntersectionObserver.bind(this);
        this.onMenuContainerClose = this.onMenuContainerClose.bind(this);
        // This.resizeObserverHandler = this.resizeObserverHandler.bind(this);
        // this.resizeObserverIsBound = false;
        // this.resizeWidth = 0;
    }
    componentDidMount() {
        const { autoPosition, isOpen } = this.props;
        isOpen && autoPosition && this.autoPositionObserver(); // Setting up auto position observer here for instances where the menu container is added/removed from DOM as opposed to visibility toggled.
    }
    componentDidUpdate(prevProps) {
        const { autoPosition, isOpen } = this.props;
        if (isOpen !== prevProps.isOpen) {
            if (isOpen) {
                autoPosition && this.autoPositionObserver(); // Setting up auto position observer here for instances where the menu container is added to the DOM, but visibility is toggled.
            }
            else {
                this.onMenuContainerClose();
            }
        }
    }
    componentWillUnmount() {
        this.onMenuContainerClose();
    }
    onMenuContainerClose() {
        this.resetMenuContainer();
        this.unobserveIntersectionObserver();
    }
    onAnimationEndCallback(e) {
        const { animationHasEnded } = this.state;
        // There are multiple animationEnd callbacks due to there being 2-3 animations applied, this logic ensures animationEndedStateCallback() is only called once.
        !animationHasEnded &&
            e.animationName.match(/menu-grow-/) &&
            this.setState({ animationHasEnded: true }, this.animationEndedStateCallback);
    }
    // This callback may appear to be doing something similar to adjustAlignment(), but it's actually only required for Safari desktop.
    // Safari desktop doesn't currently support the use of css variables inside keyframe animations. Therefore the updates to --menu-grow-x and --menu-grow-y have no impact in safari desktop.
    // In order to get the auto positioning working in safari desktop, we need to use transform: translate and set css variables for x and y to be used in the translate.
    // There's also a corresponding CSS "hack" to target safari desktop only ln 59 in uitk-menu.scss
    // The reason all of this is done once the animation is complete, is because if we do this during the animation, it causes some jankiness.
    animationEndedStateCallback() {
        const { containerRef } = this.props;
        // eslint-disable-next-line no-negated-condition
        const viewport = typeof document !== 'undefined' ? document.documentElement : null;
        const windowInnerHeight = window.innerHeight;
        if (containerRef?.current && viewport) {
            const container = containerRef.current;
            const containerBoundingClientRect = container.getBoundingClientRect();
            const viewportBoundingClientRect = viewport.getBoundingClientRect();
            let offsetX = 0;
            let offsetY = 0;
            if (containerBoundingClientRect.right > viewportBoundingClientRect.right) {
                offsetX = -Math.abs(Math.ceil(containerBoundingClientRect.right) - Math.ceil(viewportBoundingClientRect.right));
            }
            else if (containerBoundingClientRect.left < viewportBoundingClientRect.left) {
                offsetX = Math.abs(containerBoundingClientRect.left);
            }
            if (containerBoundingClientRect.bottom > windowInnerHeight) {
                offsetY = -Math.abs(Math.ceil(containerBoundingClientRect.bottom) - Math.ceil(windowInnerHeight));
            }
            if (offsetX || offsetY) {
                container.style.setProperty('--menu-offset-translate-x', `${offsetX}px`);
                container.style.setProperty('--menu-offset-translate-y', `${offsetY}px`);
            }
        }
        document.addEventListener('scroll', this.unobserveIntersectionObserver);
        window.addEventListener('resize', this.unobserveIntersectionObserver);
    }
    unobserveIntersectionObserver() {
        const { autoPosition, containerRef } = this.props;
        if (autoPosition && containerRef.current) {
            this.intersectionObserver && this.intersectionObserver.unobserve(containerRef.current);
            // This.resizeObserver &&
            //   this.resizeObserver.unobserve(this.props.containerRef!.current);
        }
    }
    autoPositionObserver() {
        const { containerRef, intersectionObserverRoot } = this.props;
        if (!(window && window.IntersectionObserver)) {
            return false;
        }
        const thresholdSet = [];
        for (let i = 0.0; i <= 1.0; i += 0.01) {
            thresholdSet.push(i);
        }
        const defaultIntersectionObserverOptions = {
            threshold: thresholdSet,
        };
        const intersectionObserverOptions = intersectionObserverRoot
            ? Object.assign(defaultIntersectionObserverOptions, {
                root: document.querySelector(intersectionObserverRoot),
            })
            : defaultIntersectionObserverOptions;
        this.intersectionObserver = new IntersectionObserver(this.autoPositionCallback, intersectionObserverOptions);
        containerRef.current && this.intersectionObserver.observe(containerRef.current);
    }
    autoPositionCallback(entries) {
        entries.forEach((entry) => {
            const formattedRatio = Math.round(parseFloat(entry.intersectionRatio.toString()) * 100) / 100;
            if (formattedRatio <= 0.99 && entry.intersectionRatio > 0) {
                this.adjustAlignment(entry);
            }
        });
    }
    adjustAlignment(entry) {
        // Set offset state to be used as measure for translate: transformX
        // !this.resizeObserverIsBound &&
        //   typeof ResizeObserver !== 'undefined' &&
        //   this.resizeObserverHandler(); // Instantiate a resize observer in case user resizes window or changes orientation
        const { context } = this.context;
        // This fixes a regression caused by https://github.expedia.biz/egds/uitk-react/pull/6320
        if (context === 'egds-date-picker-context' &&
            Math.ceil(entry.boundingClientRect.bottom) > Math.ceil(entry.intersectionRect.bottom)) {
            const currentOffsetY = Math.ceil(entry.boundingClientRect.bottom) - Math.ceil(entry.intersectionRect.bottom);
            this.setState((prevState) => ({
                offset: {
                    x: prevState.offset.x,
                    y: -Math.abs(prevState.offset.y - currentOffsetY),
                },
            }));
            return;
        }
        if (Math.ceil(entry.boundingClientRect.right) > Math.ceil(entry.intersectionRect.right)) {
            const currentOffsetX = Math.ceil(entry.boundingClientRect.right) - Math.ceil(entry.intersectionRect.right);
            this.setState((prevState) => ({
                offset: {
                    x: -Math.abs(prevState.offset.x - currentOffsetX),
                    y: prevState.offset.y,
                },
            }));
        }
        else if (Math.ceil(entry.boundingClientRect.left) < Math.ceil(entry.intersectionRect.left)) {
            const currentOffsetX = entry.boundingClientRect.left - entry.intersectionRect.left;
            this.setState((prevState) => ({
                offset: {
                    x: prevState.offset.x - currentOffsetX,
                    y: prevState.offset.y,
                },
            }));
        }
        const { adjustmentType } = this.state;
        if (Math.ceil(entry.boundingClientRect.bottom) > window.innerHeight) {
            // Checks if the menu container is taller than the viewport
            const currentOffsetY = Math.ceil(entry.boundingClientRect.bottom) - window.innerHeight;
            this.setState((prevState) => ({
                offset: {
                    x: prevState.offset.x,
                    y: 0,
                },
                adjustedHeight: entry.boundingClientRect.height - Math.abs(prevState.offset.y - currentOffsetY),
                adjustmentType: 'window',
            }));
        }
        if (Math.ceil(entry.boundingClientRect.bottom) > Math.ceil(entry.intersectionRect.bottom)) {
            // Checks if the menu container is taller than the defined root
            const currentOffsetY = Math.ceil(entry.boundingClientRect.bottom) - Math.ceil(entry.intersectionRect.bottom);
            if (entry.boundingClientRect.height > entry.intersectionRect.height) {
                // Checks if the menu container is taller than the defined root
                this.setState((prevState) => ({
                    offset: {
                        x: prevState.offset.x,
                        y: 0,
                    },
                    adjustedHeight: entry.boundingClientRect.height - Math.abs(prevState.offset.y - currentOffsetY),
                    adjustmentType: 'container',
                }));
            }
            else {
                // If the menu container is not taller than the defined root, shifts the menu container up
                this.setState((prevState) => ({
                    offset: {
                        x: prevState.offset.x,
                        y: -Math.abs(prevState.offset.y - currentOffsetY),
                    },
                }));
            }
        }
        else if (adjustmentType === 'window') {
            // Remove window adjustment if no longer needed
            this.setState(() => ({
                adjustedHeight: undefined,
                adjustmentType: undefined,
            }));
        }
    }
    resetMenuContainer() {
        this.setState({
            offset: {
                x: 0,
                y: 0,
            },
            animationHasEnded: false,
        });
        const { containerRef } = this.props;
        containerRef?.current?.style.removeProperty('--menu-offset-translate-x');
        containerRef?.current?.style.removeProperty('--menu-offset-translate-y');
        document.removeEventListener('scroll', this.unobserveIntersectionObserver);
        window.removeEventListener('resize', this.unobserveIntersectionObserver);
        // This.resizeObserver &&
        //   this.resizeObserver.unobserve(document.documentElement);
        // this.resizeObserverIsBound = false;
    }
    // Decided not to use in production as of 12/2019 due to ResizeObserver being "Editor's Draft". If the specification becomes CR / REC then this functionality can be re-introduced
    // private resizeObserverHandler() {
    //   this.resizeObserverIsBound = true;
    //   // @ts-ignore
    //   this.resizeObserver = new ResizeObserver(entries => {
    //     for (const entry of entries) {
    //       const resizeCurrentWidth = entry.contentRect.width;
    //       if (this.resizeWidth > 0 && resizeCurrentWidth > this.resizeWidth) {
    //         // If resize width is increasing - re-adjust offset
    //         if (this.props.position === PositionType.RIGHT) {
    //           this.props.containerRef!.current &&
    //             this.setState(
    //               state => ({
    //                 offset:
    //                   state.offset -
    //                   Math.ceil(
    //                     this.props.containerRef!.current!.getBoundingClientRect()
    //                       .left
    //                   )
    //               }),
    //               this.handleMenuContainerOffset
    //             );
    //         } else {
    //           this.props.containerRef!.current &&
    //             this.setState(
    //               state => ({
    //                 offset:
    //                   state.offset +
    //                   (Math.ceil(entry.contentRect.right) -
    //                     Math.ceil(
    //                       this.props.containerRef!.current!.getBoundingClientRect()
    //                         .right
    //                     ))
    //               }),
    //               this.handleMenuContainerOffset
    //             );
    //         }
    //       }
    //       this.resizeWidth = resizeCurrentWidth;
    //     }
    //   });
    //   this.resizeObserver.observe(document.documentElement);
    // }
    render() {
        const { autoPosition, containerClasses, containerRef, children, position, className, isOpen, maxHeight, width, title, allyHeadingLevel, intersectionObserverRoot, showOverTrigger, } = this.props;
        const { offset: { x, y }, adjustedHeight, } = this.state;
        const { context } = this.context;
        const wrapperClassNames = getClassNameList$3(className, containerClasses, {
            [`uitk-menu-pos-${position}`]: validatePropType(position, PositionType),
            'uitk-menu-container-autoposition': !!autoPosition,
            'uitk-menu-container-text-nowrap': !width,
            'uitk-menu-container-has-intersection-root-el': !!intersectionObserverRoot,
            'uitk-menu-container-over-trigger': !!showOverTrigger,
        });
        const divStyle = {};
        if (context === 'uitk-date-picker-context') {
            divStyle.maxHeight = `${maxHeight}px`;
            divStyle.overflow = 'auto';
        }
        else {
            let containerHeight = adjustedHeight || maxHeight;
            if (adjustedHeight && maxHeight) {
                containerHeight = Math.min(adjustedHeight, maxHeight);
            }
            if (containerHeight) {
                divStyle.maxHeight = `${containerHeight}px`;
                divStyle.overflow = 'auto';
            }
        }
        if (width) {
            divStyle.width = `${width}px`;
        }
        if (x || y) {
            divStyle['--menu-grow-x'] = `${x}px`;
            divStyle['--menu-grow-y'] = `${y}px`;
        }
        const HeadingLevel = allyHeadingLevel
            ? EGDSHeadingTags.get(allyHeadingLevel)
            : EGDSHeadingTags.get(defaultMenuHeadingTag);
        return (jsxs("div", { className: wrapperClassNames, ref: containerRef, "aria-hidden": !isOpen, style: divStyle, onAnimationEnd: this.onAnimationEndCallback, children: [title && jsx(HeadingLevel, { className: getClassNameList$3('uitk-menu-title'), children: title }), children] }));
    }
}

const getClassNameList$2 = classNames.bind(styles);
class EGDSMenuListItem extends React.Component {
    static displayName = 'EGDSMenuListItem';
    render() {
        const { analytics, className, href, iconComponent, iconName, label, Link, onClick, role, tabIndex, target, selected, disabled, to, typeAnchor, } = this.props;
        const Tag = typeAnchor ? 'a' : 'button';
        const onClickCapture = analytics && analyticsTracker.bind(this, analytics);
        const additionalProps = {
            className,
            onClick: (event) => {
                if (onClick) {
                    onClick(event);
                }
            },
            onClickCapture,
            role,
            selected,
            tabIndex,
            target,
            ...(to && { to }), // This is to prevent the "to" prop from getting set as undefined when it is not set explicitly and provided as part of the Link
        };
        /* istanbul ignore next */
        if (Link) {
            return React.cloneElement(Link, additionalProps);
        }
        additionalProps.className = getClassNameList$2(className, {
            'uitk-list-item-disabled': disabled,
        });
        return (jsxs(Tag, { ...additionalProps, ...(typeAnchor && { href }), ...(selected && { 'aria-checked': selected }), ...(disabled && { 'aria-disabled': disabled, tabIndex: -1 }), children: [iconName && iconComponent == null && (jsx(EGDSIcon, { className: getClassNameList$2('uitk-menu-list-item-icon'), name: iconName })), iconComponent && jsx("span", { className: getClassNameList$2('uitk-menu-list-item-icon'), children: iconComponent }), jsx("span", { className: getClassNameList$2('uitk-menu-list-item-label'), children: label }), selected && jsx(EGDSIcon, { className: getClassNameList$2('uitk-menu-list-item-check'), name: "check" })] }));
    }
}

const getClassNameList$1 = classNames.bind(styles);
class EGDSMenuList extends React.Component {
    static displayName = 'EGDSMenuList';
    render() {
        const { children, items, title, allyHeadingLevel } = this.props;
        const HeadingLevel = allyHeadingLevel
            ? EGDSHeadingTags.get(allyHeadingLevel)
            : EGDSHeadingTags.get(defaultMenuListHeadingTag);
        return (jsx(EGDSContextWrapper, { children: (_, data) => (jsxs("div", { "data-testid": "egds-menu-list", role: data?.isInSkipLink ? undefined : 'menu', className: getClassNameList$1('uitk-list'), children: [title && jsx(HeadingLevel, { className: getClassNameList$1('uitk-list-title'), children: title }), items &&
                        items.map((item, index) => (jsx(EGDSMenuListItem, { Link: item.Link, to: item.to, analytics: item.analytics, href: item.href, typeAnchor: item.typeAnchor, label: item.label, className: getClassNameList$1(item.className), role: data?.isInSkipLink ? undefined : item.attributes && item.attributes.role, tabIndex: item.attributes && item.attributes.tabindex, iconName: item.icon?.name, onClick: item.onClick, target: item.target, selected: item.selected, disabled: item.disabled, iconComponent: item.icon?.component }, index))), children] })) }));
    }
}

const getClassNameList = classNames.bind(styles);
const EGDSMenuIconTrigger = (props) => {
    const { analytics, className, domRef, icon, iconId, iconSize, iconTitle, onClick } = props;
    const baseClassName = 'uitk-menu-icon-trigger';
    const cssClassNames = getClassNameList(baseClassName, className);
    return (jsx("button", { type: "button", "aria-expanded": props['aria-expanded'], className: cssClassNames, onClick: onClick, onClickCapture: analyticsTracker.bind(undefined, analytics), ref: domRef, children: jsx(EGDSIcon, { name: icon, title: iconTitle, id: iconId, size: iconSize }) }));
};
EGDSMenuIconTrigger.displayName = 'EGDSMenuIconTrigger';

export { EGDSMenu as E, PositionType as P, EGDSMenuTriggerAnalyticsId as a, EGDSMenuTrigger as b, EGDSMenuContainer as c, EGDSMenuList as d, EGDSMenuListItem as e, EGDSMenuIconTrigger as f };
//# sourceMappingURL=index-chunk.esm.js.map
